import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import useValueToLabelCache from "@/hooks/useValueToLabelCache";
import { cn } from "@/lib/cn";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import Combobox from "../Combobox";
import type { ComboboxOption } from "../Combobox/types";
import Icon from "../Icon/Icon";
import { HStack } from "../Layout";
import { Button } from "../ui/button";

type Props = {
  id: string;
  value?: string;
  onChange: (option?: ComboboxOption) => void;
  fetchOptions?: (text?: string) => Promise<ComboboxOption[]>;
  disabled?: boolean;
  hideSearch?: boolean;
  allowEmpty?: boolean;
  width?: number | string;
  error?: boolean;
};

/**
 * Combobox を含む、単体で活用可能なセレクト
 */
export function ControlledSelect(props: Props) {
  const tc = useCommonTranslation();

  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const debouncedSetSearchText = useDebouncedCallback(
    (text: string) => setSearchText(text),
    [],
  );

  const valueToLabelCache = useValueToLabelCache();

  const { data: options, isPending } = useQuery({
    queryKey: ["fetchOptions", props.id, searchText],
    queryFn: () => props.fetchOptions?.(searchText),
    initialData: [],
  });

  // labelの計算：キャッシュを優先し、optionsから検索、最後にvalueをfallback
  const label = useMemo(() => {
    if (!props.value) return undefined;

    // 1. まずキャッシュから取得を試行
    const cachedLabel = valueToLabelCache.get(props.value);
    if (cachedLabel) return cachedLabel;

    // 2. optionsから検索
    const optionLabel = options?.find(
      (option) => option.value === props.value,
    )?.label;
    if (optionLabel) return optionLabel;

    // 3. fallbackとしてvalueを使用
    return props.value;
  }, [options, props.value, valueToLabelCache]);

  return (
    <Popover open={open} onOpenChange={() => setOpen(!open)}>
      <HStack>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={props.error ? true : undefined}
            className={cn(
              "justify-between overflow-hidden text-ellipsis",
              !label && "text-muted-foreground",
              "min-w-[180px]",
              typeof props.width === "number"
                ? `w-[${props.width}px]`
                : props.width,
              props.error &&
                "border-destructive focus-visible:ring-2 focus-visible:ring-destructive",
            )}
            disabled={props.disabled}
          >
            {label || tc("pleaseSelect")}
            <Icon
              iconName="ChevronDownIcon"
              className="ml-2 shrink-0 opacity-50"
            />
          </Button>
        </PopoverTrigger>
        {props.allowEmpty && props.value && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => props.onChange(undefined)}
          >
            <Icon iconName="XIcon" />
          </Button>
        )}
      </HStack>
      <PopoverContent
        className={cn(
          "max-h-96 w-full overflow-auto p-0",
          props.width && `w-[${props.width}px]`,
        )}
        align="start"
      >
        <Combobox
          type="single"
          value={props.value ?? null}
          selectionDisplayStyle="hidden"
          onValueChange={(value) => {
            const option = options?.find((option) => option.value === value);
            if (!props.allowEmpty && option == null) {
              return;
            }

            // 選択時にキャッシュを自動更新
            if (option?.value && option?.label) {
              valueToLabelCache.set(option.value, option.label);
            }

            props.onChange(option);
            setOpen(false);
          }}
          isPending={isPending}
          isFetching={isPending}
          options={options ?? []}
          onSearchChange={debouncedSetSearchText}
          valueToLabelCache={valueToLabelCache}
          hideSearch={props.hideSearch}
        />
      </PopoverContent>
    </Popover>
  );
}
